import React from "react";
import { gql, useMutation } from "@apollo/client";

import {
  AddIdentityMutationMutation,
  AddIdentityMutationMutationVariables
} from "../../../schemaTypes";

export const ADD_IDENTITY_MUTATION = gql`
  mutation AddIdentityMutation(
    $communityId: String!, 
    $communityEntityId: String,
    $idNumber: String!
  ) {
    addIdentity(
      communityId: $communityId, 
      communityEntityId: $communityEntityId,
      idNumber: $idNumber
    ) {
      errors {
        message
        path
      }
      message
    }
  }
`;

export interface WithAddIdentity {
  addIdentity: (
    variables: AddIdentityMutationMutationVariables
  ) => Promise<{
    errors: Record<string, string> | null;
    message?: string | null;
    supportMeta?: {
      code?: string;
      supportRef?: string;
    };
  }>;
}

function normalizeErrors(
  errors: { path: string; message: string }[] | null | undefined
): Record<string, string> | null {
  if (!errors || errors.length === 0) return null;
  const errMap: Record<string, string> = {};
  errors.forEach(e => {
    if (e && e.path) {
      errMap[e.path] = e.message;
    }
  });
  return errMap;
}

export function useAddIdentity(): WithAddIdentity {
  const [mutate] = useMutation<
    AddIdentityMutationMutation, 
    AddIdentityMutationMutationVariables
  >(ADD_IDENTITY_MUTATION);

  const addIdentity = React.useCallback(async (
    variables: AddIdentityMutationMutationVariables
  ) => {
    try {
      const response = await mutate({ variables });
      const result = response?.data?.addIdentity;
      if (!result) {
        return {
          errors: { general: "No response from server." },
          message: undefined,
          supportMeta: undefined,
        };
      }
      const errors = normalizeErrors(result.errors);
      const message = result.message;
      return { errors, message, supportMeta: undefined };
    } catch (err) {
      const maybeApolloErr = err as {
        message?: string;
        graphQLErrors?: Array<{ message?: string; extensions?: { code?: string; supportRef?: string } }>;
      };
      const firstGraphQLError = maybeApolloErr.graphQLErrors?.[0];
      const code = firstGraphQLError?.extensions?.code;
      const supportRef = firstGraphQLError?.extensions?.supportRef;
      const message = firstGraphQLError?.message || maybeApolloErr.message || "Unable to add identity.";

      return {
        errors: { general: message },
        message: undefined,
        supportMeta: {
          ...(typeof code === "string" ? { code } : {}),
          ...(typeof supportRef === "string" ? { supportRef } : {}),
        },
      };
    }
  }, [mutate]);

  return { addIdentity };
}